/**
 * V8.3 — local helpers that bridge the canonical `SitePreset` (still emitted by the
 * shared `<lib-site-preset-picker>` for compatibility with the wheere-inside admin
 * CRUD page) to the slim `IntegratorSitePreset` shape the integrator UI consumes.
 *
 * <p>The other Integrator* types (Site, SiteFileMeta, Gateway, Tracker) live in the
 * generated `@wheere/api-{site,gateway,tracker}` packages now — the integrator stores
 * import them directly. The only remaining projection is the preset one because the
 * shared picker hasn't been forked to a slim variant yet.
 */
import type { IntegratorSitePreset } from '@wheere/api-site';
import type { SitePreset } from './site.model';

/** Couverture non ambiguë d'un fichier de fréquences (ADR-039).
 *
 * <p>{@code c / (2 × step)} où {@code step} est l'écart entre les deux premières fréquences de
 * `freq`. Même formule que le serveur ({@code IntegratorSitePresetMapper#computeFreqCoverage}).
 * Exporté pour que la projection ci-dessous fonctionne avec le catalogue complet. */
export function calculateFreqCoverage(freq: readonly number[] | null | undefined): number | null {
  if (!freq || freq.length < 2) return null;
  const step = freq[1] - freq[0];
  if (step <= 0) return null;

  const coverage = 299_792_458 / (2 * step);
  return Math.round(coverage * 10) / 10;
}

/** Project the picker's canonical {@link SitePreset} to the generated
 * {@link IntegratorSitePreset}. Drops the frequency file and calib contents; prefers the
 * server-provided {@code freqCoverage} (slim endpoint) and falls back to computing it from
 * `frequencyFile.freq` (full admin payload). */
export function toIntegratorSitePreset(p: SitePreset): IntegratorSitePreset {
  return {
    id: p.id,
    organizationId: p.organizationId,
    name: p.name,
    comment: p.comment,
    frequencyFileName: p.frequencyFileName,
    calibFileName: p.calibFileName,
    maxAntennas: p.maxAntennas,
    size: p.size,
    isDefault: p.isDefault,
    createdAt: p.createdAt ? new Date(p.createdAt) : undefined,
    createdBy: p.createdBy,
    updatedAt: p.updatedAt ? new Date(p.updatedAt) : undefined,
    updatedBy: p.updatedBy,
    freqCoverage: p.freqCoverage ?? calculateFreqCoverage(p.frequencyFile?.freq) ?? undefined,
  };
}

/** Inverse projection: the slim {@link IntegratorSitePreset} (from `/integrator/site-presets`)
 * into the canonical {@link SitePreset} the shared picker works with. The frequency file and
 * calib contents are intentionally absent — they never reach an ORG user's browser; {@code freqCoverage}
 * carries the only derived metric the integrator UI needs. */
export function fromIntegratorSitePreset(p: IntegratorSitePreset): SitePreset {
  return {
    id: p.id ?? '',
    organizationId: p.organizationId ?? '',
    name: p.name ?? '',
    comment: p.comment,
    frequencyFileName: p.frequencyFileName,
    calibFileName: p.calibFileName,
    maxAntennas: p.maxAntennas,
    freqCoverage: p.freqCoverage ?? undefined,
    size: p.size,
    isDefault: p.isDefault,
    createdAt: p.createdAt ? new Date(p.createdAt).toISOString() : undefined,
    createdBy: p.createdBy,
    updatedAt: p.updatedAt ? new Date(p.updatedAt).toISOString() : undefined,
    updatedBy: p.updatedBy,
  };
}
